/**
 * Maps human-readable keys to translation IDs for the /tools/fib4-calculator
 * SEO landing page. New content (not from Framer XLIFF), so keys carry the
 * `fib4_` prefix directly.
 *
 * The page is gated to CH alone (`page:tools/fib4-calculator`), in all three
 * Swiss locales, so every mirror locale exempts this namespace in
 * `SKIP_NAMESPACES_PER_LOCALE` (scripts/lint-mirror-locales.ts), `de-DE`
 * included. Unlike `score2Calculator`, that exemption is a rollout state
 * rather than a clinical one: FIB-4 has no per-country calibration, so opening
 * the page to Germany needs the German copy and nothing else. See the gate's
 * own description in lib/country-gates.ts.
 *
 * No key in this namespace takes an ICU placeholder. That is deliberate: the
 * band and age-group definitions in `lib/fib4.ts` store these keys as `as
 * const` literals, and a single placeholder-bearing key in the namespace would
 * make next-intl's typed `t()` demand a `values` argument at every
 * `t(band.labelKey)` call site (the TS2554 trap documented in the BMI
 * calculator's `bmi.ts`).
 */
export const fib4CalculatorKeys = {
  // Metadata
  metaTitle: "fib4_metaTitle",
  metaDescription: "fib4_metaDescription",

  // Hero
  heading: "fib4_heading", // "FIB-4 calculator"
  subheading: "fib4_subheading",

  // Calculator widget
  calcTitle: "fib4_calcTitle",
  calcAgeLabel: "fib4_calcAgeLabel",
  calcAstLabel: "fib4_calcAstLabel",
  calcAltLabel: "fib4_calcAltLabel",
  calcEnzymeHint: "fib4_calcEnzymeHint",
  calcPlateletsLabel: "fib4_calcPlateletsLabel",
  calcPlateletsHint: "fib4_calcPlateletsHint",
  calcUnitYears: "fib4_calcUnitYears", // "years"
  calcUnitUl: "fib4_calcUnitUl", // "U/L"
  calcUnitGl: "fib4_calcUnitGl", // "G/L"
  calcSubmit: "fib4_calcSubmit",
  calcReset: "fib4_calcReset",
  calcResultLabel: "fib4_calcResultLabel", // "Your FIB-4 score"
  calcEmptyState: "fib4_calcEmptyState",
  calcOutOfRange: "fib4_calcOutOfRange",
  calcAgeOutOfRange: "fib4_calcAgeOutOfRange",
  calcPlateletsUnit: "fib4_calcPlateletsUnit",
  calcThresholdsLabel: "fib4_calcThresholdsLabel",
  calcScaleCaption: "fib4_calcScaleCaption",
  calcYoungAgeNote: "fib4_calcYoungAgeNote",
  calcEligibilityNote: "fib4_calcEligibilityNote",
  calcDisclaimer: "fib4_calcDisclaimer",

  // Quiz hand-off, shown in the result panel once Calculate has run
  quizHeading: "fib4_quizHeading",
  quizBody: "fib4_quizBody",
  quizCta: "fib4_quizCta",

  // Result bands (shared by the result card and the reference table)
  bandLow: "fib4_bandLow",
  bandIndeterminate: "fib4_bandIndeterminate",
  bandHigh: "fib4_bandHigh",

  meaningLow: "fib4_meaningLow",
  meaningIndeterminate: "fib4_meaningIndeterminate",
  meaningHigh: "fib4_meaningHigh",

  // Age groups the cut-offs are split by
  ageUnder65: "fib4_ageUnder65",
  age65plus: "fib4_age65plus",

  // Threshold cells, one per age group x band
  thrUnder65Low: "fib4_thrUnder65Low",
  thrUnder65Indeterminate: "fib4_thrUnder65Indeterminate",
  thrUnder65High: "fib4_thrUnder65High",
  thr65plusLow: "fib4_thr65plusLow",
  thr65plusIndeterminate: "fib4_thr65plusIndeterminate",
  thr65plusHigh: "fib4_thr65plusHigh",

  // Threshold reference table
  tableHeading: "fib4_tableHeading",
  tableIntro: "fib4_tableIntro",
  tableColAge: "fib4_tableColAge",
  tableNote: "fib4_tableNote",

  // What FIB-4 is
  whatLabel: "fib4_whatLabel",
  whatHeading: "fib4_whatHeading",
  whatP1: "fib4_whatP1",
  whatP2: "fib4_whatP2",
  whatP3: "fib4_whatP3",

  // What goes into it
  inputsLabel: "fib4_inputsLabel",
  inputsHeading: "fib4_inputsHeading",
  inputsIntro: "fib4_inputsIntro",
  input1Title: "fib4_input1Title",
  input1Body: "fib4_input1Body",
  input2Title: "fib4_input2Title",
  input2Body: "fib4_input2Body",
  input3Title: "fib4_input3Title",
  input3Body: "fib4_input3Body",
  input4Title: "fib4_input4Title",
  input4Body: "fib4_input4Body",

  // Who it is for, who it is not for
  whoHeading: "fib4_whoHeading",
  whoIntro: "fib4_whoIntro",
  whoForTitle: "fib4_whoForTitle",
  whoFor1: "fib4_whoFor1",
  whoFor2: "fib4_whoFor2",
  whoFor3: "fib4_whoFor3",
  whoNotForTitle: "fib4_whoNotForTitle",
  whoNotFor1: "fib4_whoNotFor1",
  whoNotFor2: "fib4_whoNotFor2",
  whoNotFor3: "fib4_whoNotFor3",
  whoNotFor4: "fib4_whoNotFor4",
  whoNote: "fib4_whoNote",

  // Units, the half of this page that is market-specific
  unitsHeading: "fib4_unitsHeading",
  unitsP1: "fib4_unitsP1",
  unitsP2: "fib4_unitsP2",
  unitsTipTitle: "fib4_unitsTipTitle",
  unitsTipBody: "fib4_unitsTipBody",

  // Limitations
  limitsLabel: "fib4_limitsLabel",
  limitsHeading: "fib4_limitsHeading",
  limitsIntro: "fib4_limitsIntro",
  limit1Title: "fib4_limit1Title",
  limit1Body: "fib4_limit1Body",
  limit2Title: "fib4_limit2Title",
  limit2Body: "fib4_limit2Body",
  limit3Title: "fib4_limit3Title",
  limit3Body: "fib4_limit3Body",
  limit4Title: "fib4_limit4Title",
  limit4Body: "fib4_limit4Body",

  // Membership section
  memLabel: "fib4_memLabel",
  memHeading: "fib4_memHeading",
  memBody: "fib4_memBody",
  memFeature1: "fib4_memFeature1",
  memFeature2: "fib4_memFeature2",
  memFeature3: "fib4_memFeature3",
  memFeature4: "fib4_memFeature4",
  memFeature5: "fib4_memFeature5",
  memCtaPrimary: "fib4_memCtaPrimary",
  memCtaSecondary: "fib4_memCtaSecondary",
  memFootnote: "fib4_memFootnote",

  // FAQ fallback (used until page-tagged FAQs exist in the CMS)
  faqHeading: "fib4_faqHeading",
  faqQ1: "fib4_faqQ1",
  faqA1: "fib4_faqA1",
  faqQ2: "fib4_faqQ2",
  faqA2: "fib4_faqA2",
  faqQ3: "fib4_faqQ3",
  faqA3: "fib4_faqA3",
  faqQ4: "fib4_faqQ4",
  faqA4: "fib4_faqA4",
  faqQ5: "fib4_faqQ5",
  faqA5: "fib4_faqA5",
  faqQ6: "fib4_faqQ6",
  faqA6: "fib4_faqA6",

  // Closing CTA
  ctaHeading: "fib4_ctaHeading",
  ctaBody: "fib4_ctaBody",
  ctaPrimary: "fib4_ctaPrimary",
  ctaSecondary: "fib4_ctaSecondary",

  // Legal / medical
  sourceLabel: "fib4_sourceLabel",
  sourceBody: "fib4_sourceBody",
  disclaimer: "fib4_disclaimer",
} as const
